import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { authApi } from '../api/client.js';
import { isApiError, userErrorMessage } from '../api/errors.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { rememberChosenPlan } from './chosen-plan.js';
import { ConsentCheckbox } from '../components/ConsentCheckbox.js';
import { Field } from '../components/Field.js';
import { FormMessage } from '../components/FormMessage.js';
import { SubmitButton } from '../components/SubmitButton.js';
import type { RegisterValues } from '../auth/schemas.js';
import { useAuthSchemas } from '../auth/useAuthSchemas.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';

export function RegisterPage(): JSX.Element {
  const { register: registerSchema } = useAuthSchemas();
  const { t } = useI18n();
  const legal = useLegal();
  const queryClient = useQueryClient();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // A plan picked on the pricing page survives the detour through the confirmation email.
  useEffect(() => rememberChosenPlan(globalThis.location?.search ?? ''), []);
  const registration = useQuery({
    queryKey: ['registration-status'],
    queryFn: authApi.registrationStatus,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({ resolver: zodResolver(registerSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      /*
       * The edition of the texts this build is showing goes with the clickwrap:
       * the record is evidence of what the person was shown, so it is the client
       * that reports it, not the server that guesses it.
       */
      await authApi.register({ ...values, termsVersion: legal.documentVersionId });
      // Ответ одинаков и для свободного, и для занятого адреса (01-SPEC.md §5.2),
      // поэтому экран всегда показывает одно и то же сообщение.
      setSentTo(values.email);
    } catch (error: unknown) {
      if (isApiError(error) && error.code === 'registration_closed') {
        queryClient.setQueryData(['registration-status'], { registrationEnabled: false });
        return;
      }
      setFormError(
        isApiError(error) && error.code === 'weak_password'
          ? t.auth.register.weakPassword
          : userErrorMessage(error, t),
      );
    }
  });

  if (registration.isPending) {
    return (
      <AuthLayout title={t.auth.register.title}>
        <p role="status">{t.common.loading}</p>
      </AuthLayout>
    );
  }

  if (registration.isError) {
    return (
      <AuthLayout title={t.auth.register.title} footer={<Link to="/login">{t.auth.signIn}</Link>}>
        <FormMessage kind="error">{t.common.unexpectedError}</FormMessage>
      </AuthLayout>
    );
  }

  if (!registration.data.registrationEnabled) {
    return (
      <AuthLayout
        title={t.auth.register.closedTitle}
        subtitle={t.auth.register.closedSubtitle}
        footer={<Link to="/login">{t.auth.signIn}</Link>}
      >
        <p className="text-center text-sm text-[var(--kitup-color-muted)]">
          {t.auth.register.closedText}
        </p>
      </AuthLayout>
    );
  }

  if (sentTo) {
    // The address is echoed back so a typo is visible now rather than after a day of waiting.
    return (
      <AuthLayout
        title={t.auth.register.sentTitle}
        footer={<Link to="/login">{t.auth.signIn}</Link>}
      >
        <div className="grid gap-4">
          <FormMessage kind="success">{t.auth.register.sentTo(sentTo)}</FormMessage>
          <p className="text-sm text-[var(--kitup-color-muted)]">
            {t.auth.register.wrongAddress}{' '}
            <button type="button" className="underline" onClick={() => setSentTo(null)}>
              {t.auth.register.startOver}
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t.auth.register.title}
      footer={
        <>
          {t.auth.haveAccount} <Link to="/login">{t.auth.signIn}</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? <FormMessage kind="error">{formError}</FormMessage> : null}

        <Field
          label={t.common.name}
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />

        <Field
          label={t.common.email}
          type="email"
          autoComplete="email"
          placeholder={t.common.emailPlaceholder}
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label={t.common.password}
          type="password"
          autoComplete="new-password"
          hint={t.auth.register.passwordHint}
          error={errors.password?.message}
          {...register('password')}
        />

        {/*
          Two explicit ticks, not one "by continuing you agree" line. Clickwrap that
          holds up turns on an affirmative act, and the second box is the age gate
          (§4.2, §6.4). It does not ask about parental authority: that is a claim about
          a particular child, and it is made — and enforced — at photo upload (§3.2).
        */}
        <ConsentCheckbox
          label={
            <>
              {t.auth.register.termsPrefix}{' '}
              <Link className="underline" to={legal.routes.terms}>
                {t.auth.register.offerLink}
              </Link>{' '}
              {t.auth.register.legalJoin}{' '}
              <Link className="underline" to={legal.routes.privacy}>
                {t.auth.register.privacyLink}
              </Link>
              .
            </>
          }
          error={errors.acceptedTerms?.message}
          {...register('acceptedTerms')}
        />

        <ConsentCheckbox
          label={t.auth.register.adultLabel}
          error={errors.adultAck?.message}
          {...register('adultAck')}
        />

        <SubmitButton label={t.auth.register.submit} pending={isSubmitting} />
      </form>
    </AuthLayout>
  );
}
